import React, {
  FC,
  ReactElement,
  useContext,
  useEffect,
  useState,
} from "react";
import { useIntl, defineMessages } from "react-intl";

import { useErrorController } from "@natera/platform/lib/hooks";
import { useDialog } from "@natera/platform/lib/hooks";
import {
  ErrorProvider,
  NotificationContext,
  SignUpContext,
  UserContext,
  ServiceContext,
  Language,
  UppAuthContext,
} from "@app/provider";
import {
  SignInWithAuthenticationProviders,
  AcknowledgmentsDialog,
  LinkHeap,
  NeedHelpLink,
  Separator,
} from "@app/components";

import SpinnerView from "@app/components/spinnerView";
import acknowledgements from "@etc/acknowledgements.json";
import SignUpByPasswordForm from "./signUpByPasswordForm";
import {
  usePortalRedirects,
  useRecaptchaSubmit,
  useTokenSourceFromQuery,
  useTokenizedLinks,
  useSignupIntent,
  RecaptchaTokens,
} from "@app/hooks";
import { SignUpByPasswordProps } from "@app/provider/signUp";
import { ResultCodes } from "@app/service/resultCodes";
import { useSignInContext } from "@app/provider/signIn";
import SendActivationEmailDialog from "@app/pages/public/signUp/sendActivationEmailDialog";
import { IntlShape } from "react-intl/src/types";
import { AddNotification } from "@app/provider/notification";
import { HEAP_EVENTS, HeapEventLocation } from "@app/provider/types";
import { ACKNOWLEDGMENT_FLOW } from "@app/components/acknowledgmentsDialog/acknowledgmentsDialog";
import { LinkSource } from "@app/hooks/useTokenizedLinks";

import "./signUp.scss";

const RECAPTCHA_SIGNUP_ACTION = "signup";

// Codes that useRecaptchaSubmit already handles (arms the v2 challenge or
// notifies) — the signup page must not also raise its generic error for these.
const RECAPTCHA_HANDLED_CODES: string[] = [
  ResultCodes.RECAPTCHA_V2_REQUIRED,
  ResultCodes.RECAPTCHA_VALIDATION_FAILED,
  ResultCodes.RECAPTCHA_RETRYABLE_FAILURE,
];

const messages = defineMessages({
  welcomeSignUpTitlePasswordActivate: {
    id: "welcomeSignUpTitlePasswordActivate",
    defaultMessage: "Create a Natera Patient Portal Account",
  },
  signUpUnrecognizedError: {
    id: "signUpUnrecognizedError",
    defaultMessage: "We're sorry. Something went wrong.",
  },
  signUpUserNetworkError: {
    id: "signUpUserNetworkError",
    defaultMessage:
      "We're sorry. An error occurred when setting up your profile.",
  },
  signUpSupportContact: {
    id: "signUpSupportContact",
    defaultMessage: "Contact support",
  },
  signUpForbiddenDomainTitle: {
    id: "signUpForbiddenDomainTitle",
    defaultMessage: "You are trying to use a Natera email address to sign up.",
  },
  signUpForbiddenDomainMessage: {
    id: "signUpForbiddenDomainMessage",
    defaultMessage:
      'Please either select the "Continue with Google" option, or sign up with email and password using a non-Natera email address.',
  },
});

interface ActivationEmailProps {
  email: string;
  password: string;
  language: Language;
}

export const checkAccess = (
  email: string,
  intl: IntlShape,
  addNotification: AddNotification
): boolean => {
  const forbiddenDomain = "@natera.com";

  if (
    email.includes(forbiddenDomain) &&
    process.env.NODE_ENV !== "development"
  ) {
    addNotification({
      title: intl.formatMessage(messages.signUpForbiddenDomainTitle),
      message: intl.formatMessage(messages.signUpForbiddenDomainMessage),
      type: "warning",
    });
    return false;
  }
  return true;
};

const SignUp: FC = () => {
  const intl = useIntl();

  const {
    signUpByPasswordError,
    signUpByPasswordNetworkError,
    signUpData,
    setSignUpData,
    signUpByPassword,
    sendActivationCode,
  } = useContext(SignUpContext);
  const { isLoading: userIsLoading, loadProfile } = useContext(UserContext);
  const { handleOtpRequest } = useSignInContext();

  const {
    acquireTokens,
    handleRecaptchaError,
    promoteToV2Challenge,
    resetV2Challenge,
    v2Challenge,
    RecaptchaV3UnavailableError,
  } = useRecaptchaSubmit({
    action: RECAPTCHA_SIGNUP_ACTION,
    v2ContainerClassName: "sign-up__recaptcha-v2",
    v2TestId: "signup-recaptcha-v2",
  });

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [initialEmail, setInitialEmail] = useState<string | undefined>();
  // The token's own email — the baseline for "did the user change the email?".
  // Kept separate from initialEmail (the form prefill) so a changed email held
  // in signUpData (e.g. after a prior OTP initiation) doesn't poison the check.
  const [tokenEmail, setTokenEmail] = useState<string | undefined>();
  const [hasAcknowledged, setHasAcknowledged] = useState(!!signUpData);

  const { toActivation } = usePortalRedirects();

  const { getUserEmailByToken } = useTokenizedLinks();
  const { setActivationEmail } = useContext(UppAuthContext);
  // UPP-5865: intentToken resolves to the in-app proof (5864) or the guest-flow
  // `si` handle carried in the URL, so a guest-flow sign-up validates its DOB
  // proof too; handleReverificationRequired recovers from a rejected proof.
  const { intentToken, handleReverificationRequired } = useSignupIntent();
  const { token, tokenSource } = useTokenSourceFromQuery();

  const getInitialEmail = async () => {
    // Always resolve the token's own email for an email-source link — even when
    // signUpData already holds a (possibly changed) email from a prior OTP
    // initiation — so the change-detection in processSignUp compares against the
    // ORIGINAL token email, not the changed one. initialEmail (the form prefill)
    // still keeps the last-entered/changed value for UX.
    if (token && tokenSource === LinkSource.EMAIL) {
      try {
        const userEmail = await getUserEmailByToken(token, intentToken);
        setTokenEmail(userEmail);
        setInitialEmail(signUpData?.email ?? userEmail);
      } catch (err) {
        // A guest-flow-preloaded intent that's already invalid/expired surfaces
        // here first; route the user back to DOB re-verification (UPP-5864/5865).
        handleReverificationRequired(err);
      }
    } else if (signUpData?.email) {
      setInitialEmail(signUpData.email);
    }
  };

  React.useEffect(() => {
    getInitialEmail();
  }, [tokenSource]);

  const { sessionService } = useContext(ServiceContext);
  const { addNotification } = useContext(NotificationContext);

  const errorController = useErrorController();
  const acknowledgementsDialog = useDialog(AcknowledgmentsDialog);
  const sendActivationEmailDialog = useDialog(SendActivationEmailDialog);

  useEffect(() => {
    if (signUpByPasswordNetworkError) {
      acknowledgementsDialog.close();
      sendActivationEmailDialog.close();
      addNotification({
        type: "error",
        message: intl.formatMessage(messages.signUpUserNetworkError),
        actions: (
          <LinkHeap
            target="_blank"
            key="privacyPolicy"
            rel="noreferrer"
            to={acknowledgements.links.contactUs}
            heapEventName={HEAP_EVENTS.upp_click_contactnatera}
            heapEventProps={{
              location: HeapEventLocation.update_info_error,
            }}
          >
            {intl.formatMessage(messages.signUpSupportContact)}
          </LinkHeap>
        ),
      });
    }

    // reCAPTCHA outcomes (v2 required / validation failed / retryable) are
    // owned by useRecaptchaSubmit — it arms the v2 challenge or notifies the
    // user itself. Surfacing the generic "something went wrong" error on top
    // of that produces a spurious page-level error alongside the v2 widget.
    if (
      signUpByPasswordError &&
      !RECAPTCHA_HANDLED_CODES.includes(signUpByPasswordError) &&
      // UPP-5864: this code drives a silent bounce back to DOB re-verification;
      // don't stack a generic page-level error on top of it.
      signUpByPasswordError !== ResultCodes.IDENTITY_REVERIFICATION_REQUIRED
    ) {
      addNotification({
        type: "error",
        message: intl.formatMessage(messages.signUpUnrecognizedError),
      });
    }
  }, [signUpByPasswordError, signUpByPasswordNetworkError]);

  const validateEmailAccess = (email: string): boolean => {
    return checkAccess(email, intl, addNotification);
  };

  const handleCreateAccount = async (
    props: SignUpByPasswordProps,
    tokensOverride?: RecaptchaTokens
  ) => {
    setIsLoading(true);
    let captchaTokens: RecaptchaTokens;
    try {
      captchaTokens = tokensOverride ?? (await acquireTokens());
    } catch (err) {
      setIsLoading(false);
      if (err instanceof RecaptchaV3UnavailableError) {
        promoteToV2Challenge(err.reason, async (tokens) => {
          await handleCreateAccount(props, tokens);
        });
        return;
      }
      throw err;
    }
    try {
      const { data } = await signUpByPassword({ ...props, ...captchaTokens });
      const result = data?.signUpByPassword;

      if (!result?.isTokenValid) {
        throw new Error("token flow unavailable");
      }

      if (await sessionService.getToken()) {
        await sessionService.clearToken();
      }

      const email = result.user?.email ?? props.email;
      if (email) {
        await sessionService.login({ ...props, email });
        await loadProfile();
        setSignUpData(null);
        resetV2Challenge();
      }
    } catch (err) {
      const handled = handleRecaptchaError(err, async (tokens) => {
        await handleCreateAccount(props, tokens);
      });
      if (handled) {
        return;
      }
      // UPP-5864: a missing/expired/invalid DOB proof must NOT degrade into the
      // activation/OTP flow (confusing UX + a signal to a brute-force attacker).
      // Notify the user and bounce back to the DOB step to re-verify (mints a
      // fresh intent).
      if (handleReverificationRequired(err)) {
        return;
      }
      if (props.email) {
        await handleSendActivationCode({
          email: props.email,
          password: props.password,
          language: props.language,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const processSignUp = async (props: ActivationEmailProps) => {
    const { email, password, language } = props;
    const isOriginalTokenEmail =
      token &&
      password &&
      tokenSource === LinkSource.EMAIL &&
      tokenEmail &&
      email.toLowerCase() === tokenEmail.toLowerCase();

    if (isOriginalTokenEmail) {
      await handleCreateAccount({
        email,
        password,
        language,
        token,
        intentToken,
      });
    } else {
      await handleSendActivationCode(props);
    }
  };

  const handleAgreeAcknowledgements = (props: ActivationEmailProps) => () => {
    setHasAcknowledged(true);
    // Close the dialog up front — the happy path navigates away so the modal
    // disappears implicitly, but the v2 fallback keeps the user on /signup
    // and the v2 widget renders inline beneath the form. Leaving the modal
    // open would cover the widget and force a manual dismiss.
    acknowledgementsDialog.close();
    processSignUp(props);
  };

  const openAcknowledgementDialog = async (props: ActivationEmailProps) => {
    const { email } = props;

    if (!validateEmailAccess(email)) {
      return;
    }

    if (hasAcknowledged) {
      processSignUp(props);
      return;
    }

    acknowledgementsDialog.open({
      userEmail: email,
      onAgreeAcknowledgements: handleAgreeAcknowledgements(props),
      onCloseDialog: acknowledgementsDialog.close,
      flow: ACKNOWLEDGMENT_FLOW.ACCOUNT,
    });
  };

  const handleSendActivationCode = async (
    props: ActivationEmailProps,
    tokensOverride?: RecaptchaTokens
  ) => {
    setIsLoading(true);

    setActivationEmail(props.email);

    let captchaTokens: RecaptchaTokens;
    try {
      captchaTokens = tokensOverride ?? (await acquireTokens());
    } catch (err) {
      setIsLoading(false);
      if (err instanceof RecaptchaV3UnavailableError) {
        promoteToV2Challenge(err.reason, async (tokens) => {
          await handleSendActivationCode(props, tokens);
        });
        return;
      }
      throw err;
    }

    try {
      const result = await sendActivationCode({
        email: props.email,
        language: props.language,
        ...captchaTokens,
      });
      const activated = result?.data?.sendActivationCode;

      setSignUpData({
        email: props.email,
        password: props.password,
      });

      if (!activated) {
        const normalizedEmail = props.email.trim().toLowerCase();
        await handleOtpRequest(normalizedEmail, {
          fromSignUpFlow: true,
        });
        return;
      }
      resetV2Challenge();
      toActivation();
    } catch (err) {
      const handled = handleRecaptchaError(err, async (tokens) => {
        await handleSendActivationCode(props, tokens);
      });
      if (handled) {
        return;
      }
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const title: ReactElement = (
    <section className="title__container">
      <h1>{intl.formatMessage(messages.welcomeSignUpTitlePasswordActivate)}</h1>
    </section>
  );

  const authenticationProviders: ReactElement = (
    <SignInWithAuthenticationProviders
      invite={token}
      tokenSource={tokenSource}
    />
  );

  const signUpByPasswordForm: ReactElement = (
    <section className="form__container">
      <ErrorProvider controller={errorController}>
        <SignUpByPasswordForm
          setValidationError={errorController.setValidationError}
          clearValidationError={errorController.clearValidationError}
          clearErrors={errorController.clearErrors}
          onSubmit={openAcknowledgementDialog}
          initialEmail={initialEmail}
          initialPassword={signUpData?.password}
        />
      </ErrorProvider>
    </section>
  );

  const isLoadingContext = userIsLoading || isLoading;

  return (
    <article className="sign-up__container">
      <SpinnerView isLoading={isLoadingContext} />
      {acknowledgementsDialog.getDialog()}
      {sendActivationEmailDialog.getDialog()}
      {title}
      {signUpByPasswordForm}
      {v2Challenge}
      <Separator />
      {authenticationProviders}
      <NeedHelpLink />
    </article>
  );
};

export default SignUp;
